import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Container as MuiContainer,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StyleFielderTitleTypography } from '~/FielderElements/FielderElements.styles';
function widthToDialogConfig(width) {
  switch (width) {
    case 'sm':
    case 'md':
    case 'lg':
    case 'xl':
      return { fullWidth: true, maxWidth: width };
    case 'Fielder-ultra-wide':
      return { fullWidth: false, maxWidth: false };
    default:
      return { fullWidth: true, maxWidth: 'md' };
  }
}
export default function Popup(
  {
    title,
    icon,
    image,
    closeButton = true,
    onClose,
    size = 'md',
    buttonText,
    requiredFields,
    buttonOnClick,
    width = 'md',
    show = true,
    centerprop,
    autoHeightMax = '90vh',
    letScroll,
    paperSx,
    contentSx,
    containerSx,
    actionsSx,
    children,
  }) {
  const verticallyCentered = centerprop == null;
  const { fullWidth, maxWidth } = useMemo(() => widthToDialogConfig(width), [width]);
  const isUltraWide = width === 'Fielder-ultra-wide';

  const handleRequestClose = useCallback(() => {
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  const wantsTallScrollArea = size === 'xl' || size === 'lg';

  return (
    <Dialog
      open={!!show}
      onClose={handleRequestClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      scroll={letScroll ? 'paper' : 'body'}
      sx={{
        '& .MuiDialog-container': {
          alignItems: verticallyCentered ? 'center' : 'flex-start',
        },
      }}
      slotProps={{
        paper: {
          component: 'div',
          sx: {
            ...(isUltraWide && {
              width: '96vw',
              maxWidth: 'min(1600px, 96vw)',
            }),
            maxHeight: autoHeightMax,
            ...paperSx,
          },
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1.25,
            px: 2,
          }}
        >
          {icon}
          {image}
          {title && (
            <StyleFielderTitleTypography variant="subtitle1">
              {title}
            </StyleFielderTitleTypography>
          )}
          {closeButton && (
            <IconButton
              id="popup-close-button"
              aria-label="close"
              onClick={handleRequestClose}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ px: 0, ...contentSx }}>
        <Box sx={{ mx: 2, overflowX: 'hidden' }}>
          {wantsTallScrollArea ? (
            <Box
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                maxHeight: autoHeightMax,
              }}
            >
              <MuiContainer disableGutters sx={{ px: 0, ...(containerSx || {}) }}>
                {children}
              </MuiContainer>
            </Box>
          ) : (
            <MuiContainer disableGutters sx={{ px: 0, ...(containerSx || {}) }}>
              {children}
            </MuiContainer>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      {buttonText && (
        <DialogActions
          sx={{
            px: 2,
            pb: 2,
            pt: 0,
            display: 'block',
            ...actionsSx,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
            <Button
              id={`popup-button-${buttonText}`}
              variant="contained"
              color="primary"
              onClick={buttonOnClick}
              sx={{
                minWidth: 200,
                height: 48,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: 16,
                px: 4,
              }}
            >
              {buttonText}
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
}
Popup.propTypes = {
  title: PropTypes.node,
  icon: PropTypes.node,
  image: PropTypes.node,
  closeButton: PropTypes.bool,
  onClose: PropTypes.func,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  buttonText: PropTypes.string,
  requiredFields: PropTypes.any,
  buttonOnClick: PropTypes.func,
  width: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'Fielder-ultra-wide']),
  show: PropTypes.bool,
  centerprop: PropTypes.any,
  autoHeightMax: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  letScroll: PropTypes.bool,
  paperSx: PropTypes.object,
  contentSx: PropTypes.object,
  containerSx: PropTypes.object,
  actionsSx: PropTypes.object,
  children: PropTypes.node,
};
Popup.defaultProps = {
  closeButton: true,
  size: 'md',
  width: 'md',
  show: true,
  autoHeightMax: '90vh',
  letScroll: false,
};
//Original code:
/*import React, { useCallback } from 'react';
import Scrollbars from 'react-custom-scrollbars-2';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button, Box, Container as MuiContainer, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import 'FielderUtils/style.css';
import { StyleFielderTitleTypography } from '~/FielderElements/FielderElements.styles';

function widthToMaxWidth(width) {
  switch (width) {
    case 'sm':
    case 'md':
    case 'lg':
    case 'xl':
      return width;
    case 'Fielder-ultra-wide':
      return false;
    default:
      return 'md';
  }
}
export default function Popup(props) {
  const {
    title,
    icon,
    image,
    closeButton,
    onClose,
    size = 'md',
    buttonText,
    requiredFields,
    buttonOnClick,
    width = 'md',
    show = true,
    centerprop,
    autoHeightMax = '90vh',
    dialogClassName,
    letScroll,
    propsContainer,
    children,
  } = props;
  const verticallyCentered = centerprop == null;
  const maxWidth = widthToMaxWidth(width);
  const isUltraWide = width === 'Fielder-ultra-wide';
  const handleRequestClose = useCallback(() => {
    if (typeof onClose === 'function') {
      onClose();
    }
  }, [onClose]);
  return (
    <Dialog
      open={!!show}
      onClose={handleRequestClose}
      fullWidth={!isUltraWide}
      maxWidth={maxWidth || 'xl'}
      scroll={letScroll ? 'paper' : 'body'}
      //scroll="paper"
      sx={{
        '& .MuiDialog-container': { alignItems: verticallyCentered ? 'center' : 'flex-start' },
      }}
      slotProps={{
        paper: {
          className: dialogClassName,
          sx: {
            ...(isUltraWide ? { maxWidth: 'min(1600px, 96vw)', width: '96vw' } : {}),
            maxHeight: autoHeightMax,
           },
          //sx: isUltraWide ? { maxWidth: 'min(1600px, 96vw)', width: '96vw' } : undefined,
        },
      }}
    >
<DialogTitle sx={{ p: 0 }}>
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 1.25,
      px: 2,
    }}
  >
    {title && (
      <StyleFielderTitleTypography variant={'subtitle1'}>
        {title}
      </StyleFielderTitleTypography>
    )}

    {closeButton && (
      <IconButton
        id={'popup-close-button'}
        aria-label="close"
        onClick={handleRequestClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
    )}
  </Box>
</DialogTitle>
{<DialogContent dividers sx={{ px: 0 }}>
  <Box sx={{ mx: 2, overflowX: 'hidden' }}>
    {(size === 'xl' || size === 'lg') ? (
      <Scrollbars autoHeight autoHeightMax={autoHeightMax} style={{ overflowX: 'hidden' }}>
        {isUltraWide ? (
          <Box className="Fielder-ultra-wide">{children}</Box>
        ) : (
          <MuiContainer disableGutters className={propsContainer} sx={{ px: 0 }}>
            {children}
          </MuiContainer>
        )}
      </Scrollbars>
    ) : (
      isUltraWide ? (
        <Box className="Fielder-ultra-wide">{children}</Box>
      ) : (
        <MuiContainer disableGutters className={propsContainer} sx={{ px: 0 }}>
          {children}
        </MuiContainer>
      )
    )}
  </Box>
</DialogContent>}
{(buttonText) && (
  <DialogActions sx={{ px: 2, pb: 2, pt: 0, display: 'block' }}>
    {buttonText && (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
        <Button
          id={`popup-button-${buttonText}`}
          variant="contained"
          className="Fielder-button popup-footer-btn"
          onClick={buttonOnClick}
        >
          {buttonText}
        </Button>
      </Box>
    )}
  </DialogActions>
)}
</Dialog>
);
}
*/
